import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";
import { LogIn, Loader2 } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_video-tagging-lab/artifacts/we05yb1x_WC%20Logo%202018%20Outlined_White.png";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Landing per role — must stay in sync with App.js fallbackHome().
  //   client → their scope folder
  //   editor → All videos (cross-folder tooling, no dashboard)
  //   admin  → Dashboard
  const homePath = (u) => {
    if (u?.role === "client" && u?.client_scope_folder_id) return `/folders/${u.client_scope_folder_id}`;
    if (u?.role === "editor") return "/library";
    return "/";
  };

  if (user) return <Navigate to={homePath(user)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const loggedIn = await login(email, password);
      navigate(homePath(loggedIn), { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex grain-overlay relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#09090b] via-[#0a0a0c] to-[#09090b]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[var(--brand-accent)]/[0.03] blur-3xl" />

      <div className="relative z-10 flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-10">
            <img src={LOGO} alt="Whiplash" className="h-20 w-auto mb-2" />
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mt-2">Digital Asset Management</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5 bg-zinc-950/40 border border-zinc-800/60 rounded-2xl p-8 backdrop-blur-sm">
            <div>
              <h1 className="text-2xl font-heading font-semibold tracking-tight">Welcome back</h1>
              <p className="text-sm text-zinc-500 mt-1">Sign in to access your video library</p>
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider text-zinc-400">Email</label>
              <input
                data-testid={TID.loginEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@whiplash.com"
                className="w-full bg-black/40 border border-zinc-800 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60 focus:ring-1 focus:ring-[var(--brand-accent)]/30 transition"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider text-zinc-400">Password</label>
              <input
                data-testid={TID.loginPassword}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full bg-black/40 border border-zinc-800 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60 focus:ring-1 focus:ring-[var(--brand-accent)]/30 transition"
              />
            </div>

            {error && <div className="text-sm text-red-400 bg-red-950/30 border border-red-900/40 rounded px-3 py-2">{error}</div>}

            <button
              data-testid={TID.loginSubmit}
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--brand-accent)] text-black font-semibold rounded-md py-2.5 text-sm hover:bg-[var(--brand-accent)]/90 transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <p className="text-center text-xs text-zinc-600 mt-6">
            Whiplash Film &amp; Creative · DAM v1
          </p>
        </div>
      </div>
    </div>
  );
}
